import { Helmet } from 'react-helmet-async'
import { useLocation } from 'react-router-dom'

const SITE_NAME = 'Digipalz'
const DEFAULT_DESCRIPTION =
  'Digipalz teaches kids online safety, digital literacy, and cyber awareness through fun, interactive lessons, quizzes, and games. Trusted by parents, teachers, and schools.'

function normalizePageTitle(title) {
  if (!title) return ''
  return String(title)
    .replace(/\s*([|–—-])\s*Digipalz\s*$/i, '')
    .trim()
}

const DOMAIN_MAP = {
  'digipalz.io': 'https://www.digipalz.io',
  'www.digipalz.io': 'https://www.digipalz.io',
  'digipalz.ca': 'https://www.digipalz.ca',
  'www.digipalz.ca': 'https://www.digipalz.ca'
}

function getBaseUrl() {
  if (typeof window === 'undefined') return 'https://www.digipalz.io'
  return DOMAIN_MAP[window.location.hostname] || 'https://www.digipalz.io'
}

function SEO({
  title,
  description = DEFAULT_DESCRIPTION,
  image,
  url,
  type = 'website',
  noIndex = false,
  exactTitle = false,
  jsonLd = null
}) {
  const location = useLocation()
  const baseUrl = getBaseUrl()
  const defaultImage = `${baseUrl}/assets/images/Home.webp`
  const resolvedImage = image || defaultImage
  const fullTitle = title
    ? exactTitle
      ? String(title).trim()
      : `${normalizePageTitle(title)} | ${SITE_NAME}`
    : `${SITE_NAME} — Online Safety Education for Kids`

  // Auto-detect path from React Router if url prop is not explicitly passed
  const resolvedPath = url ?? location.pathname
  const pathSegment = resolvedPath === '/' ? '/' : resolvedPath
  const canonicalUrl = `${baseUrl}${pathSegment}`
  const alternateIo = `https://www.digipalz.io${pathSegment}`
  const alternateCa = `https://www.digipalz.ca${pathSegment}`

  const resolvedJsonLd = jsonLd
    ? {
        ...jsonLd,
        url: jsonLd.url ? jsonLd.url.replace('https://www.digipalz.io', baseUrl) : canonicalUrl
      }
    : null

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {noIndex && <meta name="robots" content="noindex, nofollow" />}
      <link rel="canonical" href={canonicalUrl} />
      <link rel="alternate" hreflang="en-US" href={alternateIo} />
      <link rel="alternate" hreflang="en-CA" href={alternateCa} />
      <link rel="alternate" hreflang="x-default" href={alternateIo} />

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={resolvedImage} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />

      {/* Twitter / X */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={resolvedImage} />

      {/* JSON-LD structured data passed from the page */}
      {resolvedJsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(resolvedJsonLd)}
        </script>
      )}
    </Helmet>
  )
}

export default SEO
